import { defineStore } from 'pinia'
import proxmoxMachinesApi from '../api/proxmoxMachinesApi'
import OperatingSystem from '../enums/operatingSystem'
import type { NetworkInterface } from '../interfaces/NetworkInterface'
import type { ProxmoxMachine } from '../interfaces/ProxmoxMachine'

interface State {
    _machine: ProxmoxMachine | null
    networkInterfaces: NetworkInterface[]
    isNetworkInfoLoading: boolean
}

export const useMachineStore = defineStore('machine', {
    state: (): State => ({
        _machine: null,
        networkInterfaces: [],
        isNetworkInfoLoading: true,
    }),

    getters: {
        machine: (state: State): ProxmoxMachine => {
            if (!state._machine) {
                throw new Error('Machine has not been initialized.')
            }

            return state._machine
        },

        isMachineInitialized: (state: State): boolean => {
            return !!state._machine
        },

        isRunningOnAndroid(): boolean {
            return this.machine.operating_system === OperatingSystem.ANDROID_11
        },

        isRunningOnWindows(): boolean {
            return (
                this.machine.operating_system === OperatingSystem.WINDOWS_10
                || this.machine.operating_system === OperatingSystem.WINDOWS_11
                || this.machine.operating_system
                    === OperatingSystem.WINDOWS_SERVER_2022
                || this.machine.operating_system
                    === OperatingSystem.WINDOWS_SERVER_2025
            )
        },

        isRunningOnLinux(): boolean {
            return (
                this.machine.operating_system === OperatingSystem.DEBIAN_11
                || this.machine.operating_system
                    === OperatingSystem.UBUNTU_22_04
                || this.machine.operating_system
                    === OperatingSystem.UBUNTU_24_04
            )
        },
    },

    actions: {
        init(machine: ProxmoxMachine) {
            this._machine = machine
        },

        async suspend() {
            if (
                !this.machine.is_sleep_mode_allowed
                || this.machine.proxmox_config.has_gpu
            ) {
                return
            }

            try {
                await proxmoxMachinesApi.suspendMachine(this.machine.id)
            } catch {
                // Toast already displayed by http utility
            }
        },

        async reboot() {
            try {
                await proxmoxMachinesApi.rebootMachine(this.machine.id)
            } catch {
                // Toast already displayed by http utility
            }
        },

        async shutdown() {
            try {
                await proxmoxMachinesApi.shutdownMachine(this.machine.id)
            } catch {
                // Toast already displayed by http utility
            }
        },

        async fetchNetworkInfo() {
            this.isNetworkInfoLoading = true
            this.networkInterfaces = []

            try {
                const response = await proxmoxMachinesApi.getNetworkInfo(
                    this.machine.id,
                    { displayToastOnError: false }
                )
                this.networkInterfaces = response.data
            } catch {
                this.networkInterfaces = []
            } finally {
                this.isNetworkInfoLoading = false
            }
        },
    },
})
